import type {
  AuthSession,
  Commitment,
  Conversation,
  ConversationReport,
  Customer,
  OrganizationMember,
  OrganizationRole,
  OrganizationSummary,
  Message,
  RetellConnection,
  RetellWebhookConfig,
  UserContext,
  Visit,
  WhatsAppConnection,
  WhatsAppWebhookConfig,
} from "./types";
import { authenticatedFetch } from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
export const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

export function apiConfigured() {
  return Boolean(API_BASE_URL);
}

export async function listRemoteConversations(session: AuthSession): Promise<Conversation[]> {
  const body = await request<{ items: Array<Record<string, unknown>> }>("/conversations", session);
  return body.items.map(normalizeConversation);
}

export async function getRemoteConversation(
  conversationId: string,
  session: AuthSession,
): Promise<Conversation & { visits?: Visit[]; reports?: ConversationReport[] }> {
  const body = await request<{ item: Record<string, unknown> }>(
    `/conversations/${encodeURIComponent(conversationId)}`,
    session,
  );
  return normalizeConversation(body.item) as Conversation & { visits?: Visit[]; reports?: ConversationReport[] };
}

export async function importRemoteConversation(
  conversation: Conversation,
  session: AuthSession,
): Promise<Conversation> {
  const body = await request<{ item: Record<string, unknown> }>("/imports/whatsapp", session, {
    method: "POST",
    body: JSON.stringify({
      id: conversation.id,
      company: conversation.company,
      customer: conversation.customer,
      site: conversation.site,
      contactName: conversation.contactName,
      phone: conversation.phone,
      assignedTo: conversation.assignedTo,
      summary: conversation.summary,
      priority: conversation.priority,
      status: conversation.status,
      tags: conversation.tags,
      nextAction: conversation.nextAction ?? "",
      visitRequired: Boolean(conversation.visitRequired),
      reportRequired: Boolean(conversation.reportRequired),
      messages: conversation.messages.map((message) => ({
        id: message.id,
        sender: message.sender,
        body: message.body,
        sentAt: message.sentAt,
        sentAtIso: message.sentAtIso,
        direction: message.direction,
        type: message.type,
      })),
      commitments: conversation.commitments.map((item) => ({
        id: item.id,
        description: item.description,
        dueAt: item.dueAt ?? "",
        responsible: item.responsible,
        status: item.status,
        sourceMessageId: item.sourceMessageId,
      })),
    }),
  });
  return normalizeConversation(body.item);
}

export async function updateRemoteConversation(
  conversationId: string,
  changes: Partial<Pick<Conversation, "status" | "priority" | "assignedTo" | "summary" | "tags" | "nextAction" | "unreadCount" | "managedFromApp">>,
  session: AuthSession,
) {
  const body = await request<{ item: Record<string, unknown> }>(
    `/conversations/${encodeURIComponent(conversationId)}`,
    session,
    { method: "PATCH", body: JSON.stringify(changes) },
  );
  return normalizeConversation(body.item);
}

export async function requestRemoteConversationAnalysis(
  conversationId: string,
  session: AuthSession,
) {
  const body = await request<{ item: { queued: boolean } }>(
    `/conversations/${encodeURIComponent(conversationId)}/analysis`,
    session,
    { method: "POST" },
  );
  return body.item;
}

export async function sendRemoteWhatsAppText(
  conversationId: string,
  messageBody: string,
  session: AuthSession,
): Promise<Message> {
  const body = await request<{ item: Record<string, unknown> }>(
    `/conversations/${encodeURIComponent(conversationId)}/messages`,
    session,
    { method: "POST", body: JSON.stringify({ body: messageBody }) },
  );
  return normalizeMessage(body.item);
}

export async function createRemoteCommitment(
  conversationId: string,
  input: Omit<Commitment, "id" | "conversationId" | "createdAt">,
  session: AuthSession,
): Promise<Commitment> {
  const body = await request<{ item: Commitment }>(
    `/conversations/${encodeURIComponent(conversationId)}/commitments`,
    session,
    { method: "POST", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function updateRemoteCommitment(
  commitmentId: string,
  changes: Partial<Pick<Commitment, "status" | "dueAt" | "responsible" | "description" | "reviewStatus">>,
  session: AuthSession,
) {
  const body = await request<{ item: Commitment }>(
    `/commitments/${encodeURIComponent(commitmentId)}`,
    session,
    { method: "PATCH", body: JSON.stringify(changes) },
  );
  return body.item;
}

export async function createRemoteVisit(
  conversationId: string,
  visit: Visit,
  session: AuthSession,
) {
  const body = await request<{ item: Visit }>(
    `/conversations/${encodeURIComponent(conversationId)}/visits`,
    session,
    {
      method: "POST",
      body: JSON.stringify({
        title: visit.title,
        scheduledAt: visit.scheduledAt,
        assignedTo: visit.assignedTo,
        status: visit.status,
        objective: visit.objective,
        diagnosis: visit.diagnosis ?? "",
        nextSteps: visit.nextSteps,
      }),
    },
  );
  return body.item;
}

export async function createRemoteReport(
  conversationId: string,
  report: ConversationReport,
  session: AuthSession,
) {
  const body = await request<{ item: ConversationReport }>(
    `/conversations/${encodeURIComponent(conversationId)}/reports`,
    session,
    {
      method: "POST",
      body: JSON.stringify({ title: report.title, status: report.status }),
    },
  );
  return body.item;
}

export async function listRemoteVisits(session: AuthSession) {
  const body = await request<{ items: Visit[] }>("/visits", session);
  return body.items;
}

export async function listRemoteReports(session: AuthSession) {
  const body = await request<{ items: ConversationReport[] }>("/reports", session);
  return body.items;
}

export async function remoteDashboard(session: AuthSession) {
  return request<{
    conversations: number;
    open: number;
    urgent: number;
    pendingCommitments: number;
    scheduledVisits: number;
    reports: number;
  }>("/dashboard", session);
}

export async function getRemoteUserContext(
  session: AuthSession,
  preferredOrganizationId?: string,
): Promise<UserContext> {
  return request<UserContext>("/me", {
    ...session,
    activeOrganizationId: preferredOrganizationId ?? session.activeOrganizationId,
  });
}

export async function createRemoteOrganization(
  input: { name: string; slug?: string },
  session: AuthSession,
): Promise<OrganizationSummary> {
  const body = await request<{ item: OrganizationSummary }>("/organizations", session, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.item;
}

export async function updateRemoteOrganization(
  organizationId: string,
  input: { name: string },
  session: AuthSession,
) {
  const body = await request<{ item: OrganizationSummary }>(
    "/organizations/" + encodeURIComponent(organizationId),
    { ...session, activeOrganizationId: organizationId },
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function listRemoteOrganizationMembers(
  organizationId: string,
  session: AuthSession,
): Promise<OrganizationMember[]> {
  const body = await request<{ items: OrganizationMember[] }>(
    "/organizations/" + encodeURIComponent(organizationId) + "/members",
    { ...session, activeOrganizationId: organizationId },
  );
  return body.items;
}

export async function inviteRemoteOrganizationMember(
  organizationId: string,
  input: { email: string; role: OrganizationRole },
  session: AuthSession,
): Promise<OrganizationMember & { invitationSent?: boolean }> {
  const body = await request<{ item: OrganizationMember & { invitationSent?: boolean } }>(
    "/organizations/" + encodeURIComponent(organizationId) + "/invitations",
    { ...session, activeOrganizationId: organizationId },
    { method: "POST", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function updateRemoteOrganizationMember(
  organizationId: string,
  userId: string,
  input: { role?: OrganizationRole; status?: "active" | "suspended" },
  session: AuthSession,
): Promise<OrganizationMember> {
  const body = await request<{ item: OrganizationMember }>(
    "/organizations/" + encodeURIComponent(organizationId) + "/members/" + encodeURIComponent(userId),
    { ...session, activeOrganizationId: organizationId },
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function listRemoteCustomers(session: AuthSession): Promise<Customer[]> {
  const body = await request<{ items: Customer[] }>("/customers", session);
  return body.items;
}

export async function createRemoteCustomer(
  input: { name: string; externalId?: string; sites?: string[]; status?: "active" | "inactive" },
  session: AuthSession,
): Promise<Customer> {
  const body = await request<{ item: Customer }>("/customers", session, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.item;
}

export async function updateRemoteCustomer(
  customerId: string,
  input: { name?: string; externalId?: string; sites?: string[]; status?: "active" | "inactive" },
  session: AuthSession,
): Promise<Customer> {
  const body = await request<{ item: Customer }>(
    `/customers/${encodeURIComponent(customerId)}`,
    session,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function getRemoteWhatsAppWebhookConfig(session: AuthSession): Promise<WhatsAppWebhookConfig> {
  return request<WhatsAppWebhookConfig>("/whatsapp/config", session);
}

export async function listRemoteWhatsAppConnections(session: AuthSession): Promise<WhatsAppConnection[]> {
  const body = await request<{ items: WhatsAppConnection[] }>("/whatsapp/connections", session);
  return body.items;
}

export async function createRemoteWhatsAppConnection(
  input: {
    label: string;
    wabaId: string;
    phoneNumberId: string;
    displayPhoneNumber?: string;
    appId?: string;
    appSecret: string;
    accessToken: string;
  },
  session: AuthSession,
): Promise<WhatsAppConnection> {
  const body = await request<{ item: WhatsAppConnection }>("/whatsapp/connections", session, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.item;
}

export async function updateRemoteWhatsAppConnection(
  connectionId: string,
  input: {
    label?: string;
    displayPhoneNumber?: string;
    appId?: string;
    appSecret?: string;
    accessToken?: string;
    status?: "pending" | "paused";
  },
  session: AuthSession,
): Promise<WhatsAppConnection> {
  const body = await request<{ item: WhatsAppConnection }>(
    "/whatsapp/connections/" + encodeURIComponent(connectionId),
    session,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function activateRemoteWhatsAppConnection(
  connectionId: string,
  session: AuthSession,
): Promise<WhatsAppConnection> {
  const body = await request<{ item: WhatsAppConnection }>(
    "/whatsapp/connections/" + encodeURIComponent(connectionId) + "/activate",
    session,
    { method: "POST" },
  );
  return body.item;
}

export async function getRemoteRetellWebhookConfig(session: AuthSession): Promise<RetellWebhookConfig> {
  return request<RetellWebhookConfig>("/retell/config", session);
}

export async function listRemoteRetellConnections(session: AuthSession): Promise<RetellConnection[]> {
  const body = await request<{ items: RetellConnection[] }>("/retell/connections", session);
  return body.items;
}

export async function createRemoteRetellConnection(
  input: { label: string; businessPhone: string; whatsAppPhoneNumberId?: string; apiKey: string },
  session: AuthSession,
): Promise<RetellConnection> {
  const body = await request<{ item: RetellConnection }>("/retell/connections", session, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.item;
}

export async function updateRemoteRetellConnection(
  connectionId: string,
  input: { label?: string; whatsAppPhoneNumberId?: string; apiKey?: string; status?: "active" | "paused" },
  session: AuthSession,
): Promise<RetellConnection> {
  const body = await request<{ item: RetellConnection }>(
    "/retell/connections/" + encodeURIComponent(connectionId),
    session,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.item;
}

async function request<T>(path: string, session: AuthSession, init: RequestInit = {}): Promise<T> {
  if (!API_BASE_URL) throw new Error("API_NOT_CONFIGURED");
  const response = await authenticatedFetch(`${API_BASE_URL}${path}`, session, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof body?.error === "string" ? body.error : `HTTP_${response.status}`;
    const providerCode = Number(body?.providerCode);
    throw new Error(Number.isFinite(providerCode) && providerCode > 0 ? code + "::" + providerCode : code);
  }
  return body as T;
}

function normalizeConversation(item: Record<string, unknown>): Conversation {
  return {
    id: String(item.id ?? item.conversationId ?? ""),
    tenantId: String(item.tenantId ?? ""),
    company: String(item.company ?? item.companyId ?? "Empresa"),
    customer: String(item.customer ?? item.contactName ?? "Cliente"),
    site: String(item.site ?? "Sin instalación"),
    contactName: String(item.contactName ?? item.customer ?? "Contacto"),
    phone: String(item.phone ?? item.contactPhone ?? ""),
    summary: String(item.summary ?? "Conversación pendiente de análisis."),
    lastMessage: String(item.lastMessage ?? item.lastMessagePreview ?? ""),
    lastMessageAt: String(item.lastMessageAt ?? item.updatedAt ?? ""),
    unreadCount: Number(item.unreadCount ?? 0),
    priority: isPriority(item.priority) ? item.priority : "normal",
    status: isStatus(item.status) ? item.status : "open",
    assignedTo: String(item.assignedTo ?? "Sin asignar"),
    tags: Array.isArray(item.tags) ? item.tags.map(String) : [],
    commitments: Array.isArray(item.commitments) ? (item.commitments as Commitment[]) : [],
    messages: Array.isArray(item.messages)
      ? (item.messages as Array<Record<string, unknown>>).map(normalizeMessage)
      : [],
    source: ((item.source ?? item.channel) as Conversation["source"]) ?? "manual",
    createdAt: typeof item.createdAt === "string" ? item.createdAt : undefined,
    updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : undefined,
    nextAction: typeof item.nextAction === "string" ? item.nextAction : undefined,
    visitRequired: Boolean(item.visitRequired),
    reportRequired: Boolean(item.reportRequired),
    phoneNumberId: typeof item.phoneNumberId === "string" ? item.phoneNumberId : undefined,
    managedFromApp: item.managedFromApp === true,
    lastInboundAt: typeof item.lastInboundAt === "string" ? item.lastInboundAt : undefined,
    analysisStatus: ["queued", "analyzing", "ready", "error"].includes(String(item.analysisStatus))
      ? item.analysisStatus as Conversation["analysisStatus"]
      : undefined,
    analysisUpdatedAt: typeof item.analysisUpdatedAt === "string" ? item.analysisUpdatedAt : undefined,
    analysisThrough: typeof item.analysisThrough === "string" ? item.analysisThrough : undefined,
    analysisModel: typeof item.analysisModel === "string" ? item.analysisModel : undefined,
    analysisConfidence: typeof item.analysisConfidence === "number" ? item.analysisConfidence : undefined,
    analysisError: typeof item.analysisError === "string" ? item.analysisError : undefined,
    analysisSuggestedStatus: isStatus(item.analysisSuggestedStatus) ? item.analysisSuggestedStatus : undefined,
    analysisSuggestedAssignee: typeof item.analysisSuggestedAssignee === "string" ? item.analysisSuggestedAssignee : undefined,
    analysisMessageCount: typeof item.analysisMessageCount === "number" ? item.analysisMessageCount : undefined,
    analysisLockedFields: Array.isArray(item.analysisLockedFields) ? item.analysisLockedFields.map(String) : undefined,
  };
}

function normalizeMessage(message: Record<string, unknown>): Message {
  const direction = message.direction === "outbound" ? "outbound" : "inbound";
  const type = ["text", "audio", "image", "document", "video", "location", "unknown", "system", "call"].includes(String(message.type))
    ? message.type as Message["type"]
    : "unknown";
  const deliveryStatus = ["accepted", "sent", "delivered", "read", "failed"].includes(String(message.deliveryStatus))
    ? message.deliveryStatus as Message["deliveryStatus"]
    : undefined;
  return {
    ...message,
    id: String(message.id ?? message.messageId ?? ""),
    body: String(message.body ?? ""),
    sender: String(message.sender ?? message.contactName ?? (direction === "outbound" ? "Equipo" : "Contacto")),
    sentAt: String(message.sentAt ?? message.timestamp ?? ""),
    sentAtIso: typeof message.sentAtIso === "string" ? message.sentAtIso : undefined,
    direction,
    type,
    deliveryStatus,
  } as Message;
}

function isPriority(value: unknown): value is Conversation["priority"] {
  return ["urgent", "high", "normal", "low"].includes(String(value));
}

function isStatus(value: unknown): value is Conversation["status"] {
  return ["open", "waiting", "scheduled", "resolved"].includes(String(value));
}
